// The Mail workspace (Session AUB, email system Phase 4): /office/mail. ONE place for the office's email, replacing the
// Email engine, Enquiries, Emails to answer and Work orders pages (their old addresses redirect here, App.tsx).
//
// Lanes as tabs (Item 1), each decided LIVE by the server (server/src/modules/mail/lanes.ts): Reply (a reply is ready to
// check and send), Person (the engine would not draft it), Work orders, Filed and All. Then the secondary tabs, each the
// existing screen reused rather than rebuilt: Chases due (Session AUH, Phase 7: ChasesTab.tsx), Awaiting replies (the inbox
// sweep's view of conversations where we asked something, formerly labelled Chases due), Calls
// (unmatched phone enquiries), Bulk approve, Clear old, and the engine's Library, Draft quality and Mailbox history.
//
// Session AUG (email system Phase 6, Item 2) adds Metrics, last: how the mailbox is being handled (MetricsTab.tsx).
//
// Session AUD (Item 2) adds the Outbox (OutboxTab.tsx) between Filed and All: job-stage notices to customers, sent by a
// person in a batch through a branded confirm (DE-1).
//
// The active tab lives in the URL (?tab=), so a link, a redirect or a refresh lands on the right one.
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../../lib/api";
import type { EmailEngineStatus, EnquiryListRow, MailAssignee, MailCounts, MailMessageLane } from "../../../lib/api";
import { friendlyApiError } from "../../../lib/friendlyApiError";
import { MAIL_LANE_LABELS } from "../../../lib/mailWorkspace";
import { PageHeader } from "../../../components/PageHeader";
import { Loader } from "../../../components/Loader";
import { StatusStrip, LibraryTab, QualityTab, HistoryTab } from "../EmailEnginePage";
import { BulkApproveView } from "../EnquiriesPage";
import { EnquiryBacklogPanel } from "../EnquiryBacklogPanel";
import InboxTriagePage from "../InboxTriagePage";
import WorkOrdersPage from "../WorkOrdersPage";
import { MailLaneView } from "./MailLaneView";
import { PhoneEnquiriesPanel } from "./PhoneEnquiriesPanel";
import { OutboxTab } from "./OutboxTab";
import { MetricsTab } from "./MetricsTab";
import { ChasesTab } from "./ChasesTab";

export const MAIL_TABS = ["reply", "person", "work", "filed", "outbox", "all", "chases", "triage", "calls", "bulk", "clear", "library", "quality", "history", "metrics"] as const;
export type MailTab = (typeof MAIL_TABS)[number];

// Session AUD (Item 2): the Outbox is a primary tab, after Filed and before All. Its notices are not conversations, so it is
// not one of MAIL_LANE_LABELS (the server-mirrored lane vocabulary) and is labelled here.
// Session AVZ: three lists up front (Reply ready, Needs you, Filed). Everything else sits behind "More", still one click away
// and still addressed by ?tab=, so every link and redirect lands where it always did.
const PRIMARY: MailTab[] = ["reply", "person", "filed"];
const MORE_PRIMARY: MailTab[] = ["work", "outbox", "all"];
const FRONT_LABELS: Partial<Record<MailTab, string>> = { reply: "Reply ready", person: "Needs you" };
const SECONDARY_LABELS: Record<Exclude<MailTab, "reply" | "person" | "work" | "filed" | "outbox" | "all">, string> = {
  chases: "Chases due",
  triage: "Awaiting replies",
  calls: "Calls",
  bulk: "Bulk approve",
  clear: "Clear old",
  library: "Library",
  quality: "Draft quality",
  history: "Mailbox history",
  metrics: "Metrics",
};

export function mailTabFrom(v: string | null): MailTab {
  return (MAIL_TABS as readonly string[]).includes(v ?? "") ? (v as MailTab) : "reply";
}

function tabLabel(t: MailTab): string {
  if (t === "outbox") return "Outbox";
  if (FRONT_LABELS[t]) return FRONT_LABELS[t]!;
  return t in MAIL_LANE_LABELS ? MAIL_LANE_LABELS[t as keyof typeof MAIL_LANE_LABELS] : SECONDARY_LABELS[t as keyof typeof SECONDARY_LABELS];
}

function chipCls(on: boolean): string {
  return `shrink-0 whitespace-nowrap rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition min-h-11 sm:min-h-0 ${
    on ? "border-ele-green text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-strong"
  }`;
}

export default function MailWorkspacePage() {
  const [params, setParams] = useSearchParams();
  const tab = mailTabFrom(params.get("tab"));
  const [counts, setCounts] = useState<MailCounts | null>(null);
  const [assignees, setAssignees] = useState<MailAssignee[]>([]);
  const [status, setStatus] = useState<EmailEngineStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreActive = !PRIMARY.includes(tab);
  const showMore = moreOpen || moreActive;

  const loadCounts = useCallback(async () => {
    try {
      setCounts(await api.mail.counts());
    } catch {
      // A badge that could not be read shows no number, never a reassuring zero.
      setCounts(null);
    }
  }, []);
  const loadStatus = useCallback(async () => {
    try {
      setStatus(await api.emailEngine.status());
      setStatusError(null);
    } catch (err) {
      setStatusError(friendlyApiError(err, "Could not read the email engine's status."));
    }
  }, []);

  useEffect(() => {
    void loadCounts();
    void loadStatus();
    api.mail
      .assignees()
      .then((r) => setAssignees(r.assignees))
      .catch(() => setAssignees([]));
  }, [loadCounts, loadStatus]);

  function selectTab(next: MailTab) {
    // A fresh tab starts from its own defaults: filters carried in the address (customerId, outcome) belong to the link
    // that brought the office here, not to every tab after it.
    const p = new URLSearchParams();
    p.set("tab", next);
    setParams(p, { replace: true });
  }

  const badge = (t: MailTab): number | null => {
    if (!counts) return null;
    if (t === "reply") return counts.reply;
    if (t === "person") return counts.person;
    if (t === "work") return counts.work;
    if (t === "filed") return counts.filed;
    // A server older than Session AUD sends no outbox count: no badge, never a reassuring zero.
    if (t === "outbox") return typeof counts.outbox === "number" ? counts.outbox : null;
    return null;
  };

  const lane: MailMessageLane | null = tab === "reply" || tab === "person" || tab === "filed" || tab === "all" ? tab : null;

  return (
    <div className="min-w-0">
      <PageHeader
        title="Mail"
        subtitle="Replies ready to check and send, emails that need you, and what was filed. Everything else is under More."
        className="mb-4"
      />
      <StatusStrip
        status={status}
        error={statusError}
        onChecked={() => {
          void loadStatus();
          void loadCounts();
          setReloadKey((k) => k + 1);
        }}
        onDraftingChanged={() => void loadStatus()}
      />

      <div className="mb-4 min-w-0 border-b border-ele-hairline pb-2">
        <div data-hscroll role="tablist" aria-label="Mail sections" className="-mx-1 flex min-w-0 items-center gap-2 overflow-x-auto px-1 pb-1">
          {PRIMARY.map((t) => {
            const n = badge(t);
            return (
              <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => selectTab(t)} className={chipCls(tab === t)} data-testid={`mail-tab-${t}`}>
                {tabLabel(t)}
                {n != null && (
                  <span className="ml-1 opacity-80" data-testid={`mail-count-${t}`}>
                    ({n})
                  </span>
                )}
              </button>
            );
          })}
          <button
            type="button"
            aria-expanded={showMore}
            onClick={() => setMoreOpen((v) => !v)}
            className={chipCls(moreActive)}
            data-testid="mail-more"
          >
            {showMore ? "Less" : "More"}
          </button>
          <div className={showMore ? "flex items-center gap-2" : "hidden"} data-testid="mail-more-tabs">
            <span aria-hidden className="mx-1 h-5 w-px shrink-0 bg-ele-hairline" />
            {MORE_PRIMARY.map((t) => {
              const n = badge(t);
              return (
                <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => selectTab(t)} className={chipCls(tab === t)} data-testid={`mail-tab-${t}`}>
                  {tabLabel(t)}
                  {n != null && (
                    <span className="ml-1 opacity-80" data-testid={`mail-count-${t}`}>
                      ({n})
                    </span>
                  )}
                </button>
              );
            })}
            {(Object.keys(SECONDARY_LABELS) as Array<keyof typeof SECONDARY_LABELS>).map((t) => (
              <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => selectTab(t)} className={chipCls(tab === t)} data-testid={`mail-tab-${t}`}>
                {SECONDARY_LABELS[t]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {lane && <MailLaneView key={`${lane}-${reloadKey}`} lane={lane} assignees={assignees} onCountsStale={() => void loadCounts()} />}
      {tab === "work" && <WorkOrdersPage embedded />}
      {tab === "outbox" && <OutboxTab onChanged={() => void loadCounts()} />}
      {tab === "chases" && <ChasesTab />}
      {tab === "triage" && <InboxTriagePage embedded />}
      {tab === "calls" && <PhoneEnquiriesPanel />}
      {tab === "bulk" && <BulkApproveTab onChanged={() => void loadCounts()} />}
      {tab === "clear" && <EnquiryBacklogPanel onChanged={() => void loadCounts()} />}
      {tab === "library" && <LibraryTab />}
      {tab === "quality" && <QualityTab />}
      {tab === "history" && <HistoryTab />}
      {tab === "metrics" && <MetricsTab />}
    </div>
  );
}

// The Bulk approve view reads the enquiry list exactly as the Enquiries page did, and hands it to the same component.
function BulkApproveTab({ onChanged }: { onChanged: () => void }) {
  const [enquiries, setEnquiries] = useState<EnquiryListRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.enquiries.list();
      setEnquiries(res.enquiries);
    } catch (err) {
      setError(friendlyApiError(err, "Could not load the enquiries."));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <div role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center">
        <p className="text-ele-strong">{error}</p>
        <button type="button" onClick={() => void load()} className="mt-3 rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong max-sm:min-h-11">
          Retry
        </button>
      </div>
    );
  }
  if (!enquiries) {
    return (
      <Loader label="Loading the enquiries..." rows={6} />
    );
  }
  return (
    <BulkApproveView
      enquiries={enquiries}
      onRefresh={async () => {
        await load();
        onChanged();
      }}
    />
  );
}
